import {
  budgetCategories as sharedBudgetCategories,
  vendorTypes as sharedVendorTypes,
  type EntityType,
} from "@wedding/schemas";
import {
  LayoutDashboard,
  ListChecks,
  CalendarRange,
  WalletCards,
  Handshake,
  Gem,
  Flower2,
  Gift,
  Users,
  Armchair,
  Clock3,
  Camera,
  Luggage,
  UserRoundCog,
  ShieldAlert,
  Lightbulb,
  ScrollText,
  Settings,
} from "lucide-react";

export type Field = {
  key: string;
  label: string;
  type?:
    | "text"
    | "number"
    | "currency"
    | "date"
    | "select"
    | "textarea"
    | "checkbox"
    | "relation";
  options?: string[];
  optionLabels?: Record<string, string>;
  required?: boolean;
  relationType?: EntityType;
  relationLabelKeys?: string[];
  readOnly?: boolean;
};
export type ModuleConfig = {
  type: EntityType;
  path: string;
  label: string;
  singular: string;
  description: string;
  icon: typeof ListChecks;
  fields: Field[];
  accent: string;
};
const status = ["未开始", "进行中", "待确认", "已完成", "延期", "已取消"];
export const guestSides = [
  "新郎方",
  "新娘方",
  "新郎方父母",
  "新娘方父母",
  "朋友",
  "同事",
  "双方",
];
export const guestSideLabels: Record<string, string> = {
  男方: "新郎方",
  新郎: "新郎方",
  女方: "新娘方",
  新娘: "新娘方",
  男方父母: "新郎方父母",
  女方父母: "新娘方父母",
  共同好友: "朋友",
};

export function normalizeGuestSide(value: unknown) {
  const side = String(value ?? "");
  return guestSideLabels[side] ?? side;
}
export const budgetCategories = [...sharedBudgetCategories];
export const vendorTypes = [...sharedVendorTypes];
const vendorRelation = {
  type: "relation" as const,
  relationType: "vendors" as const,
  relationLabelKeys: ["vendorName", "vendorType"],
  required: true,
};
const optionalVendorRelation = {
  type: "relation" as const,
  relationType: "vendors" as const,
  relationLabelKeys: ["vendorName", "vendorType"],
};
export const taskModuleLabels = {
  timeline: "时间倒排",
  budgets: "预算与付款",
  vendors: "供应商",
  "core-vendors": "四大金刚",
  decorations: "花艺与装饰",
  "stationery-gifts": "喜糖请柬伴手礼",
  guests: "宾客名单",
  tables: "座位安排",
  schedules: "婚礼当天流程",
  shots: "拍摄清单",
  items: "物品装箱",
  assignments: "人员分工",
  risks: "应急预案",
  ideas: "创意灵感库",
} as const satisfies Partial<Record<EntityType, string>>;
export type TaskModuleType = keyof typeof taskModuleLabels;
export const taskModuleTypes = Object.keys(
  taskModuleLabels,
) as TaskModuleType[];
export const modules: ModuleConfig[] = [
  {
    type: "tasks",
    path: "tasks",
    label: "总任务清单",
    singular: "任务",
    description:
      "聚合所有业务模块的待办；在这里或模块内修改，编辑的都是同一条记录",
    icon: ListChecks,
    accent: "gold",
    fields: [
      { key: "title", label: "任务名称", required: true },
      {
        key: "moduleType",
        label: "所属模块",
        type: "select",
        options: taskModuleTypes,
        optionLabels: taskModuleLabels,
        required: true,
      },
      { key: "status", label: "状态", type: "select", options: status },
      {
        key: "priority",
        label: "优先级",
        type: "select",
        options: ["高", "中", "低"],
      },
      { key: "dueDate", label: "截止日期", type: "date" },
      { key: "owner", label: "负责人" },
      { key: "vendorId", label: "关联供应商", ...optionalVendorRelation },
      { key: "category", label: "分类" },
      { key: "description", label: "任务说明", type: "textarea" },
      { key: "notes", label: "备注", type: "textarea" },
    ],
  },
  {
    type: "timeline",
    path: "timeline",
    label: "时间倒排",
    singular: "阶段",
    description: "以婚礼日期为锚点的倒排里程碑",
    icon: CalendarRange,
    accent: "sage",
    fields: [
      { key: "phase", label: "倒排阶段", required: true },
      { key: "suggestedStartDate", label: "建议启动", type: "date" },
      { key: "suggestedEndDate", label: "建议完成", type: "date" },
      { key: "goal", label: "核心目标" },
      { key: "keyTasks", label: "关键任务", type: "textarea" },
      { key: "owner", label: "主负责人" },
      { key: "status", label: "状态", type: "select", options: status },
    ],
  },
  {
    type: "budgets",
    path: "budget",
    label: "预算与付款",
    singular: "预算项",
    description: "统一查看预算、实际支出、已付款与待付款；金额以人民币元填写",
    icon: WalletCards,
    accent: "rose",
    fields: [
      {
        key: "category",
        label: "类别",
        type: "select",
        options: budgetCategories,
        required: true,
      },
      { key: "item", label: "项目", required: true },
      { key: "vendorId", label: "供应商", ...vendorRelation },
      { key: "budgetCents", label: "预算（元）", type: "currency" },
      { key: "quoteCents", label: "报价（元）", type: "currency" },
      { key: "depositCents", label: "约定定金（元）", type: "currency" },
      { key: "actualCents", label: "实际金额（元）", type: "currency" },
      { key: "paidCents", label: "已付款（元）", type: "currency" },
      {
        key: "outstandingCents",
        label: "待付款（元）",
        type: "currency",
        readOnly: true,
      },
      {
        key: "paymentStatus",
        label: "付款状态",
        type: "select",
        options: ["未付款", "已付定金", "部分付款", "已付清", "退款中"],
      },
      { key: "paymentDate", label: "最近付款日期", type: "date" },
      { key: "paymentDueDate", label: "下次付款日期", type: "date" },
      {
        key: "invoiceStatus",
        label: "发票状态",
        type: "select",
        options: ["无需发票", "待开票", "已开票"],
      },
      {
        key: "contractStatus",
        label: "合同状态",
        type: "select",
        options: status,
      },
      { key: "contractNo", label: "合同编号" },
      { key: "notes", label: "备注", type: "textarea" },
    ],
  },
  {
    type: "vendors",
    path: "vendors",
    label: "供应商",
    singular: "供应商",
    description: "集中维护供应商资料，预算、待办和其他清单会自动引用最新信息",
    icon: Handshake,
    accent: "blue",
    fields: [
      {
        key: "vendorType",
        label: "供应商类型",
        type: "select",
        options: vendorTypes,
        required: true,
      },
      { key: "vendorName", label: "供应商名称", required: true },
      { key: "contact", label: "联系人" },
      { key: "phone", label: "电话" },
      { key: "wechat", label: "微信" },
      { key: "budgetCents", label: "预算（元）", type: "currency" },
      { key: "quoteCents", label: "报价（元）", type: "currency" },
      { key: "depositCents", label: "约定定金（元）", type: "currency" },
      {
        key: "balanceCents",
        label: "报价尾款（元）",
        type: "currency",
        readOnly: true,
      },
      {
        key: "contractStatus",
        label: "合同状态",
        type: "select",
        options: status,
      },
      { key: "availabilityConfirmed", label: "档期已确认", type: "checkbox" },
      { key: "finalSelected", label: "最终选择", type: "checkbox" },
      { key: "risks", label: "风险点", type: "textarea" },
    ],
  },
  {
    type: "core-vendors",
    path: "core-vendors",
    label: "四大金刚",
    singular: "候选人",
    description: "候选人必须来自供应商库，改名后所有页面自动同步",
    icon: Gem,
    accent: "violet",
    fields: [
      {
        key: "category",
        label: "类别",
        type: "select",
        options: ["化妆师", "司仪", "摄影师", "摄像师"],
        required: true,
      },
      { key: "vendorId", label: "候选供应商", ...vendorRelation },
      { key: "budgetCents", label: "预算（元）", type: "currency" },
      { key: "priceCents", label: "价格（元）", type: "currency" },
      { key: "depositCents", label: "约定定金（元）", type: "currency" },
      { key: "style", label: "风格" },
      { key: "portfolioScore", label: "作品评分", type: "number" },
      { key: "communicationScore", label: "沟通评分", type: "number" },
      {
        key: "availability",
        label: "档期",
        type: "select",
        options: ["待确认", "已确认", "不可用"],
      },
      { key: "preferred", label: "首选", type: "checkbox" },
    ],
  },
  {
    type: "decorations",
    path: "decorations",
    label: "花艺与装饰",
    singular: "装饰项",
    description: "花艺供应商统一引用供应商库，避免名称漂移",
    icon: Flower2,
    accent: "sage",
    fields: [
      { key: "areaItem", label: "区域 / 物品", required: true },
      { key: "purpose", label: "用途 / 场景" },
      { key: "quantity", label: "数量" },
      { key: "colorMaterial", label: "主色 / 花材" },
      { key: "vendorId", label: "供应商", ...vendorRelation },
      { key: "budgetCents", label: "预算（元）", type: "currency" },
      { key: "quoteCents", label: "报价（元）", type: "currency" },
      { key: "depositCents", label: "约定定金（元）", type: "currency" },
      { key: "dueDate", label: "截止日期", type: "date" },
      { key: "owner", label: "负责人" },
      { key: "status", label: "状态", type: "select", options: status },
      { key: "risk", label: "风险点", type: "textarea" },
    ],
  },
  {
    type: "stationery-gifts",
    path: "stationery-gifts",
    label: "喜糖请柬伴手礼",
    singular: "物料",
    description: "类别受控，供应商统一引用供应商库",
    icon: Gift,
    accent: "rose",
    fields: [
      {
        key: "category",
        label: "类别",
        type: "select",
        options: ["喜糖", "请柬", "伴手礼", "物料"],
        required: true,
      },
      { key: "item", label: "项目", required: true },
      { key: "specification", label: "规格 / 内容" },
      { key: "quantity", label: "数量", type: "number" },
      { key: "unitPriceCents", label: "单价（元）", type: "currency" },
      { key: "budgetCents", label: "预算（元）", type: "currency" },
      {
        key: "totalBudgetCents",
        label: "数量 × 单价（元）",
        type: "currency",
        readOnly: true,
      },
      { key: "quoteCents", label: "报价（元）", type: "currency" },
      { key: "depositCents", label: "约定定金（元）", type: "currency" },
      { key: "vendorId", label: "供应商", ...vendorRelation },
      { key: "sampleDate", label: "打样确认", type: "date" },
      { key: "arrivalDate", label: "到货发放", type: "date" },
      { key: "owner", label: "负责人" },
      { key: "status", label: "状态", type: "select", options: status },
    ],
  },
  {
    type: "guests",
    path: "guests",
    label: "宾客名单",
    singular: "宾客",
    description: "邀请、回执、家属、餐食与接待安排",
    icon: Users,
    accent: "blue",
    fields: [
      { key: "name", label: "姓名", required: true },
      { key: "relationship", label: "关系" },
      {
        key: "side",
        label: "归属方",
        type: "select",
        options: guestSides,
        optionLabels: guestSideLabels,
      },
      { key: "phone", label: "手机" },
      {
        key: "invitationDelivery",
        label: "领取请柬方式",
        type: "select",
        options: ["当面领取", "邮寄"],
      },
      { key: "mailingAddress", label: "邮寄地址", type: "textarea" },
      {
        key: "invitationStatus",
        label: "请柬状态",
        type: "select",
        options: ["未发送", "已发送", "已查看", "已回复"],
      },
      {
        key: "attendance",
        label: "是否出席",
        type: "select",
        options: ["待确认", "出席", "不出席"],
      },
      { key: "familyCount", label: "家属人数", type: "number" },
      { key: "childrenCount", label: "儿童人数", type: "number" },
      { key: "specialMeal", label: "特殊餐食" },
      { key: "tableNumber", label: "桌号" },
      { key: "notes", label: "备注", type: "textarea" },
    ],
  },
  {
    type: "tables",
    path: "seating",
    label: "座位安排",
    singular: "桌位",
    description: "拖拽排座并实时检查容量与冲突",
    icon: Armchair,
    accent: "gold",
    fields: [
      { key: "tableNumber", label: "桌号", required: true },
      { key: "tableName", label: "桌名" },
      { key: "area", label: "区域" },
      { key: "capacity", label: "容量", type: "number" },
      { key: "captain", label: "桌长" },
      { key: "locked", label: "锁定", type: "checkbox" },
      { key: "notes", label: "备注", type: "textarea" },
    ],
  },
  {
    type: "schedules",
    path: "day-schedule",
    label: "婚礼当天流程",
    singular: "流程环节",
    description: "现场时间轴、倒计时与离线运行模式",
    icon: Clock3,
    accent: "gold",
    fields: [
      { key: "startTime", label: "开始时间", required: true },
      { key: "endTime", label: "结束时间" },
      { key: "segment", label: "环节", required: true },
      { key: "location", label: "地点" },
      { key: "owner", label: "负责人" },
      { key: "participants", label: "参与人员" },
      { key: "requiredItems", label: "所需物品", type: "textarea" },
      { key: "precautions", label: "注意事项", type: "textarea" },
      { key: "contingency", label: "风险预案", type: "textarea" },
      { key: "completed", label: "已完成", type: "checkbox" },
    ],
  },
  {
    type: "shots",
    path: "shots",
    label: "拍摄清单",
    singular: "镜头",
    description: "摄影摄像必拍镜头与现场完成状态",
    icon: Camera,
    accent: "violet",
    fields: [
      { key: "scene", label: "场景" },
      { key: "shotName", label: "镜头名称", required: true },
      { key: "description", label: "拍摄说明", type: "textarea" },
      { key: "timeRange", label: "时间段" },
      { key: "location", label: "地点" },
      { key: "owner", label: "拍摄组" },
      {
        key: "requiredLevel",
        label: "必拍等级",
        type: "select",
        options: ["必拍", "重要", "可选"],
      },
      { key: "videoRequired", label: "需要视频", type: "checkbox" },
      { key: "completed", label: "已完成", type: "checkbox" },
    ],
  },
  {
    type: "items",
    path: "packing",
    label: "物品装箱",
    singular: "物品",
    description: "新人、父母与现场物品全链路追踪",
    icon: Luggage,
    accent: "sage",
    fields: [
      { key: "ownerSide", label: "归属" },
      { key: "category", label: "类别" },
      { key: "item", label: "物品", required: true },
      { key: "quantity", label: "数量", type: "number" },
      {
        key: "preparationStatus",
        label: "准备状态",
        type: "select",
        options: [
          "未准备",
          "准备中",
          "已准备",
          "已装箱",
          "已带到现场",
          "已回收",
          "遗失",
        ],
      },
      { key: "owner", label: "负责人" },
      { key: "location", label: "放置位置" },
      { key: "usageTime", label: "使用时间" },
      { key: "bringToVenue", label: "带到现场", type: "checkbox" },
      { key: "recovered", label: "已回收", type: "checkbox" },
    ],
  },
  {
    type: "assignments",
    path: "assignments",
    label: "人员分工",
    singular: "分工",
    description: "父母、伴郎伴娘和工作人员责任卡",
    icon: UserRoundCog,
    accent: "blue",
    fields: [
      { key: "role", label: "角色", required: true },
      { key: "name", label: "姓名 / 候选" },
      { key: "module", label: "负责模块" },
      { key: "task", label: "具体任务", type: "textarea" },
      { key: "milestone", label: "时间节点" },
      { key: "contact", label: "联系方式" },
      { key: "backupPerson", label: "备份人" },
      { key: "precautions", label: "注意事项", type: "textarea" },
      { key: "status", label: "状态", type: "select", options: status },
    ],
  },
  {
    type: "risks",
    path: "risks",
    label: "应急预案",
    singular: "风险预案",
    description: "预防、演练与现场处置卡片",
    icon: ShieldAlert,
    accent: "rose",
    fields: [
      { key: "risk", label: "风险事项", required: true },
      {
        key: "riskLevel",
        label: "风险等级",
        type: "select",
        options: ["高", "中", "低"],
      },
      { key: "possibleTime", label: "可能发生时间" },
      { key: "prevention", label: "预防措施", type: "textarea" },
      { key: "responsePlan", label: "现场处理方案", type: "textarea" },
      { key: "owner", label: "负责人" },
      { key: "backupContact", label: "备用联系人" },
      { key: "backupItems", label: "备用物品" },
      { key: "drilled", label: "已演练", type: "checkbox" },
      { key: "status", label: "状态", type: "select", options: status },
    ],
  },
  {
    type: "ideas",
    path: "ideas",
    label: "创意灵感库",
    singular: "创意",
    description: "从灵感收集到落地执行的决策漏斗",
    icon: Lightbulb,
    accent: "violet",
    fields: [
      { key: "theme", label: "主题" },
      { key: "idea", label: "创意点", required: true },
      { key: "applicableSegment", label: "适用环节" },
      { key: "execution", label: "执行方式", type: "textarea" },
      {
        key: "budgetLevel",
        label: "预算等级",
        type: "select",
        options: ["低", "中", "高"],
      },
      { key: "owner", label: "负责人" },
      {
        key: "decisionStatus",
        label: "决策状态",
        type: "select",
        options: ["未开始", "灵感收集", "待评估", "已采纳", "已放弃", "已执行"],
      },
      { key: "referenceUrl", label: "灵感链接" },
      { key: "keywords", label: "质感关键词" },
    ],
  },
];
export const extraNav = [
  { path: "audit", label: "操作记录", icon: ScrollText },
  { path: "settings", label: "项目设置", icon: Settings },
];
export const dashboardNav = {
  path: "dashboard",
  label: "婚礼总览",
  icon: LayoutDashboard,
};
export const moduleByPath = Object.fromEntries(
  modules.map((m) => [m.path, m]),
) as Record<string, ModuleConfig>;
